import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

const RealTimeAlerts = () => {
  const [alerts, setAlerts] = useState([]);
  const [alertSettings, setAlertSettings] = useState({});
  const [showSettings, setShowSettings] = useState(false);

  useEffect(() => {
    // Mock alert data
    const mockAlerts = [
      {
        id: 1,
        type: 'service_level',
        severity: 'critical',
        title: 'Hizmet Seviyesi Düşük',
        message: 'Teknik Destek kuyruğunda hizmet seviyesi %65\'e düştü (Hedef: %80)',
        timestamp: new Date(Date.now() - 300000),
        queue: 'support',
        acknowledged: false,
        autoResolve: false
      },
      {
        id: 2,
        type: 'long_wait',
        severity: 'warning',
        title: 'Uzun Bekleme Süresi',
        message: 'Müşteri Mehmet Yılmaz 8 dakikadır bekliyor',
        timestamp: new Date(Date.now() - 180000),
        queue: 'sales',
        acknowledged: false,
        autoResolve: true
      },
      {
        id: 3,
        type: 'agent_assistance',
        severity: 'info',
        title: 'Temsilci Yardım Talebi',
        message: 'Zeynep Kaya süpervizör yardımı talep etti',
        timestamp: new Date(Date.now() - 120000),
        agent: 'Zeynep Kaya',
        acknowledged: false,
        autoResolve: false
      },
      {
        id: 4,
        type: 'system',
        severity: 'warning',
        title: 'Sistem Performansı',
        message: 'Arama kalitesi düşük - Ağ gecikmesi tespit edildi',
        timestamp: new Date(Date.now() - 60000),
        acknowledged: true,
        autoResolve: false
      },
      {
        id: 5,
        type: 'abandonment',
        severity: 'critical',
        title: 'Yüksek Terk Oranı',
        message: 'Son 30 dakikada terk oranı %12\'ye yükseldi (Hedef: %5)',
        timestamp: new Date(Date.now() - 30000),
        acknowledged: false,
        autoResolve: false
      }
    ];

    const mockSettings = {
      serviceLevelThreshold: 80,
      waitTimeThreshold: 300,
      abandonmentThreshold: 5,
      soundEnabled: true,
      emailNotifications: true,
      autoAcknowledge: false
    };

    setAlerts(mockAlerts);
    setAlertSettings(mockSettings);
  }, []);

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'bg-red-100 text-red-800 border-red-200';
      case 'warning': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'info': return 'bg-blue-100 text-blue-800 border-blue-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'critical': return 'AlertTriangle';
      case 'warning': return 'AlertCircle';
      case 'info': return 'Info';
      default: return 'Bell';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'service_level': return 'TrendingDown';
      case 'long_wait': return 'Clock';
      case 'agent_assistance': return 'UserCheck';
      case 'system': return 'Server';
      case 'abandonment': return 'UserX';
      default: return 'Bell';
    }
  };

  const formatTimestamp = (timestamp) => {
    const now = new Date();
    const diff = Math.floor((now - timestamp) / 1000);
    
    if (diff < 60) return `${diff} saniye önce`;
    if (diff < 3600) return `${Math.floor(diff / 60)} dakika önce`;
    return `${Math.floor(diff / 3600)} saat önce`;
  };

  const handleAcknowledge = (alertId) => {
    setAlerts(prev => prev?.map(alert => 
      alert?.id === alertId ? { ...alert, acknowledged: true } : alert
    ));
  };

  const handleResolve = (alertId) => {
    setAlerts(prev => prev?.filter(alert => alert?.id !== alertId));
  };

  const handleClearAll = () => {
    setAlerts(prev => prev?.filter(alert => alert?.severity === 'critical' && !alert?.acknowledged));
  };

  const unacknowledgedCount = alerts?.filter(alert => !alert?.acknowledged)?.length;
  const criticalCount = alerts?.filter(alert => alert?.severity === 'critical' && !alert?.acknowledged)?.length;

  return (
    <div className="space-y-6">
      {/* Alert Summary */}
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm">
        <div className="p-6 border-b border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-slate-900">Gerçek Zamanlı Uyarılar</h3>
              <p className="text-sm text-slate-600 mt-1">
                {unacknowledgedCount} onaylanmamış uyarı, {criticalCount} kritik
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={handleClearAll}
                className="px-3 py-2 text-sm text-slate-600 hover:text-slate-900 border border-slate-200 rounded-lg hover:bg-slate-50"
              >
                Tümünü Temizle
              </button>
              <button
                onClick={() => setShowSettings(!showSettings)}
                className="p-2 text-slate-600 hover:text-slate-900 border border-slate-200 rounded-lg hover:bg-slate-50"
              >
                <Icon name="Settings" size={16} />
              </button>
            </div>
          </div>
        </div>

        {/* Alert Settings Panel */}
        {showSettings && (
          <div className="p-6 bg-slate-50 border-b border-slate-200">
            <h4 className="text-sm font-medium text-slate-900 mb-4">Uyarı Ayarları</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">
                  Hizmet Seviyesi Eşiği (%)
                </label>
                <input
                  type="number"
                  value={alertSettings?.serviceLevelThreshold}
                  onChange={(e) => setAlertSettings(prev => ({
                    ...prev,
                    serviceLevelThreshold: parseInt(e?.target?.value)
                  }))}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">
                  Bekleme Süresi Eşiği (saniye)
                </label>
                <input
                  type="number"
                  value={alertSettings?.waitTimeThreshold}
                  onChange={(e) => setAlertSettings(prev => ({
                    ...prev,
                    waitTimeThreshold: parseInt(e?.target?.value)
                  }))}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">
                  Terk Oranı Eşiği (%)
                </label>
                <input
                  type="number"
                  value={alertSettings?.abandonmentThreshold}
                  onChange={(e) => setAlertSettings(prev => ({
                    ...prev,
                    abandonmentThreshold: parseInt(e?.target?.value)
                  }))}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            <div className="flex items-center space-x-6 mt-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={alertSettings?.soundEnabled}
                  onChange={(e) => setAlertSettings(prev => ({
                    ...prev,
                    soundEnabled: e?.target?.checked
                  }))}
                  className="mr-2"
                />
                <span className="text-sm text-slate-700">Ses Uyarıları</span>
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={alertSettings?.emailNotifications}
                  onChange={(e) => setAlertSettings(prev => ({
                    ...prev,
                    emailNotifications: e?.target?.checked
                  }))}
                  className="mr-2"
                />
                <span className="text-sm text-slate-700">E-posta Bildirimleri</span>
              </label>
            </div>
          </div>
        )}

        {/* Alerts List */}
        <div className="max-h-96 overflow-y-auto">
          {alerts?.length === 0 ? (
            <div className="p-8 text-center">
              <Icon name="CheckCircle" size={48} className="mx-auto text-green-500 mb-4" />
              <p className="text-slate-600">Aktif uyarı bulunmuyor</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-200">
              {alerts?.map((alert) => (
                <div
                  key={alert?.id}
                  className={`p-4 ${alert?.acknowledged ? 'bg-slate-50' : 'bg-white'} hover:bg-slate-50 transition-colors`}
                >
                  <div className="flex items-start space-x-3">
                    <div className={`p-2 rounded-lg ${getSeverityColor(alert?.severity)}`}>
                      <Icon name={getSeverityIcon(alert?.severity)} size={16} />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <h4 className={`text-sm font-medium ${alert?.acknowledged ? 'text-slate-500' : 'text-slate-900'}`}>
                            {alert?.title}
                          </h4>
                          <Icon name={getTypeIcon(alert?.type)} size={14} className="text-slate-400" />
                        </div>
                        <span className="text-xs text-slate-500">
                          {formatTimestamp(alert?.timestamp)}
                        </span>
                      </div>
                      
                      <p className={`text-sm mt-1 ${alert?.acknowledged ? 'text-slate-500' : 'text-slate-700'}`}>
                        {alert?.message}
                      </p>
                      
                      {(alert?.queue || alert?.agent) && (
                        <div className="flex items-center space-x-2 mt-2">
                          {alert?.queue && (
                            <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                              <Icon name="Users" size={10} className="mr-1" />
                              {alert?.queue === 'sales' ? 'Satış' :
                               alert?.queue === 'support' ? 'Teknik Destek' : 'Faturalama'}
                            </span>
                          )}
                          {alert?.agent && (
                            <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-purple-100 text-purple-800 rounded-full">
                              <Icon name="User" size={10} className="mr-1" />
                              {alert?.agent}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      {!alert?.acknowledged && (
                        <button
                          onClick={() => handleAcknowledge(alert?.id)}
                          className="p-1 text-blue-600 hover:text-blue-800 rounded"
                          title="Onayla"
                        >
                          <Icon name="Check" size={16} />
                        </button>
                      )}
                      <button
                        onClick={() => handleResolve(alert?.id)}
                        className="p-1 text-red-600 hover:text-red-800 rounded"
                        title="Çöz"
                      >
                        <Icon name="X" size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      {/* Critical Alerts Banner */}
      {criticalCount > 0 && (
        <div className="fixed bottom-4 right-4 bg-red-600 text-white p-4 rounded-lg shadow-lg max-w-sm animate-pulse">
          <div className="flex items-center space-x-2">
            <Icon name="AlertTriangle" size={20} />
            <div>
              <p className="font-medium">Kritik Uyarı!</p>
              <p className="text-sm opacity-90">
                {criticalCount} kritik uyarı dikkat gerektiriyor
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RealTimeAlerts;